import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Rocket, Handshake, Briefcase, Coins, Mail, Lock, EyeOff, Eye, Ban, XCircle, 
  AlertTriangle, Clock, RefreshCw, CheckCircle, Sparkles, Globe, Zap, BookOpen, 
  ChevronDown, Users, TrendingUp, ShieldCheck, ArrowRight, Star, BarChart3, Store,
  ChevronLeft, ChevronRight, Package, FileText, Receipt, Wrench, PaintBucket,
  Truck, Camera, GraduationCap, Scissors, Heart, MapPin, Phone, ShoppingBag,
  Layers, PieChart, CreditCard, UserPlus, Settings, Bell, Search, Filter,
  DollarSign, Target, Award, Lightbulb, Play, Youtube, MessageCircle, HelpCircle
} from 'lucide-react';

// ===== Hook: Animation au scroll (IntersectionObserver) =====
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);
  return [ref, isVisible];
};

// ===== Hook: Compteur anime =====
const useCounter = (end, duration = 2000, isVisible = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, isVisible]);
  return count;
};

// ===== Composant: Carte animee au scroll =====
const AnimatedCard = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Login = () => {
  const { login, resendVerificationEmail } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  const [heroTextIndex, setHeroTextIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const loginFormRef = useRef(null);
  const navigate = useNavigate();

  // Remplacez par l'ID de votre video YouTube (ex: dQw4w9WgXcQ)
  const YOUTUBE_VIDEO_ID = 'E2KTh_H9SAk';
  // Remplacez par l'ID de votre playlist YouTube de tutos
  const YOUTUBE_PLAYLIST_ID = 'PLipl4pPX5ODZHJPz2jevlV1W9s4YmPnqa';
  const YOUTUBE_WORKSPACE_PLAYLIST_ID = 'PLipl4pPX5ODZ2bA6DhraAItC4TVY9IQz-';

  const [statsRef, statsVisible] = useScrollAnimation();

  const countEntrepreneurs = useCounter(1200, 2000, statsVisible);
  const countProjets = useCounter(580, 2000, statsVisible);
  const countBoutiques = useCounter(350, 2000, statsVisible);
  const countServices = useCounter(900, 2000, statsVisible);

  const heroTexts = [
    'Lancez votre business',
    'Gerez votre boutique',
    'Creez des factures pro',
    'Trouvez des partenaires',
    'Offrez vos services',
  ];

  useEffect(() => {
    const accountError = localStorage.getItem('account_error');
    if (accountError) { setError(accountError); localStorage.removeItem('account_error'); }
  }, []);

  useEffect(() => {
    const t1 = setInterval(() => setCurrentTestimonial(p => (p + 1) % 5), 5000);
    const t2 = setInterval(() => setCurrentScreenshot(p => (p + 1) % 3), 4000);
    const t3 = setInterval(() => setHeroTextIndex(p => (p + 1) % heroTexts.length), 3000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
  }, []);

  const scrollToLogin = () => loginFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  const scrollToDiscover = () => document.getElementById('decouvrir')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setEmailNotVerified(false); setResendSuccess(false);
    try {
      await login(email, mot_de_passe);
      navigate('/');
    } catch (err) {
      if (err.response?.data?.email_not_verified) {
        setEmailNotVerified(true);
        setUnverifiedEmail(err.response.data.email);
        setError('Veuillez verifier votre adresse email avant de vous connecter.');
      } else {
        setError(err.response?.data?.error || 'Identifiants invalides');
      }
    } finally { setLoading(false); }
  };
  
  const handleResendVerification = async () => {
    setResendLoading(true); setResendSuccess(false);
    try { await resendVerificationEmail(unverifiedEmail || email); setResendSuccess(true); }
    catch { setError("Erreur lors de l'envoi. Reessayez plus tard."); }
    finally { setResendLoading(false); }
  };

  const features = [
    { icon: <Rocket size={28} />, title: 'Lancez votre business', desc: "Creez votre activite en quelques clics, meme sans capital ni materiel." },
    { icon: <Handshake size={28} />, title: 'Trouvez des partenaires', desc: "Connectez-vous avec des talents prets a investir ou collaborer." },
    { icon: <Briefcase size={28} />, title: 'Offrez vos services', desc: "Proposez vos competences : plomberie, design, cours, et plus." },
    { icon: <Coins size={28} />, title: 'Financements partages', desc: "Financez vos projets collectivement avec d'autres entrepreneurs." },
  ];

  const screenshots = [
    {
      title: 'Tableau de bord',
      desc: 'Vue complete de votre activite',
      color: 'from-blue-500 to-indigo-600',
      content: (
        <div className="p-4 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1 bg-white/20 rounded-xl p-3 backdrop-blur-sm">
              <div className="text-xs text-white/70">Chiffre d'affaires</div>
              <div className="text-xl font-bold text-white">2 450 000 F</div>
              <div className="text-xs text-green-300">+12% ce mois</div>
            </div>
            <div className="flex-1 bg-white/20 rounded-xl p-3 backdrop-blur-sm">
              <div className="text-xs text-white/70">Commandes</div>
              <div className="text-xl font-bold text-white">147</div>
              <div className="text-xs text-green-300">+8 aujourd'hui</div>
            </div>
          </div>
          <div className="bg-white/15 rounded-xl p-3 backdrop-blur-sm">
            <div className="text-xs text-white/70 mb-2">Ventes de la semaine</div>
            <div className="flex items-end gap-1 h-16">
              {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                <div key={i} className="flex-1 bg-white/40 rounded-t" style={{ height: `${h}%` }}></div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              {['L','M','M','J','V','S','D'].map((d, i) => (
                <span key={i} className="text-[9px] text-white/50 flex-1 text-center">{d}</span>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 bg-white/10 rounded-lg p-2">
              <div className="text-[10px] text-white/60">Stock faible</div>
              <div className="text-sm font-semibold text-yellow-300">3 produits</div>
            </div>
            <div className="flex-1 bg-white/10 rounded-lg p-2">
              <div className="text-[10px] text-white/60">Factures impayees</div>
              <div className="text-sm font-semibold text-orange-300">2 factures</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Gestion de stock',
      desc: 'Suivez vos produits en temps reel',
      color: 'from-emerald-500 to-teal-600',
      content: (
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 bg-white/15 rounded-xl p-2 backdrop-blur-sm">
            <Search size={14} className="text-white/50" />
            <span className="text-xs text-white/50">Rechercher un produit...</span>
            <Filter size={14} className="ml-auto text-white/50" />
          </div>
          {[
            { nom: 'Perceuse INGCO 850W', stock: 12, prix: '17 600 F', status: 'ok' },
            { nom: 'Niveau laser HLL156', stock: 2, prix: '40 000 F', status: 'low' },
            { nom: 'Colle C2CI 25kg', stock: 0, prix: '2 500 F', status: 'out' },
            { nom: 'Disque diamant 115mm', stock: 45, prix: '1 500 F', status: 'ok' },
            { nom: 'Compresseur 24L', stock: 3, prix: '70 000 F', status: 'low' },
          ].map((p, i) => (
            <div key={i} className="flex items-center gap-3 bg-white/10 rounded-lg p-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                p.status === 'ok' ? 'bg-green-400/20' : p.status === 'low' ? 'bg-yellow-400/20' : 'bg-red-400/20'
              }`}>
                <Package size={14} className={
                  p.status === 'ok' ? 'text-green-300' : p.status === 'low' ? 'text-yellow-300' : 'text-red-300'
                } />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-white truncate">{p.nom}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-white">{p.prix}</div>
                <div className={`text-[10px] ${
                  p.status === 'ok' ? 'text-green-300' : p.status === 'low' ? 'text-yellow-300' : 'text-red-300'
                }`}>Stock: {p.stock}</div>
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Factures & Devis',
      desc: 'Creez des documents professionnels',
      color: 'from-violet-500 to-purple-600',
      content: (
        <div className="p-4 space-y-2">
          <div className="bg-white/15 rounded-xl p-3 backdrop-blur-sm border border-white/10">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-sm font-bold text-white">Facture #FAC-2026-0147</div>
                <div className="text-[10px] text-white/50">Client: Societe ABC</div>
              </div>
              <span className="px-2 py-0.5 bg-green-400/20 text-green-300 text-[10px] rounded-full font-medium">Payee</span>
            </div>
            <div className="space-y-1.5 mb-3">
              {[
                { desc: 'Perceuse electrique 850W', prix: '35 200' },
                { desc: 'Disques diamant 115mm x10', prix: '15 000' },
                { desc: "Main d'oeuvre installation", prix: '25 000' },
              ].map((l, i) => (
                <div key={i} className="flex justify-between text-[10px] text-white/80 bg-white/5 rounded px-2 py-1">
                  <span className="truncate flex-1">{l.desc}</span>
                  <span className="ml-2 font-medium">{l.prix} F</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-2 flex justify-between">
              <span className="text-xs text-white/60">Total TTC</span>
              <span className="text-sm font-bold text-white">75 200 F</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-white">24</div>
              <div className="text-[10px] text-white/50">Factures ce mois</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-green-300">89%</div>
              <div className="text-[10px] text-white/50">Taux de paiement</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const sampleProjects = [
    { title: 'Boutique de materiaux de construction', author: 'Ibrahim D.', location: 'Abidjan, Cocody', budget: '2 500 000 F', seeking: 'Associe avec local commercial', category: 'Commerce', progress: 65, icon: <Store size={20} />, color: 'bg-blue-500' },
    { title: 'Atelier de couture moderne', author: 'Awa S.', location: 'Bouake, Centre', budget: '800 000 F', seeking: 'Investisseur ou partenaire couturiere', category: 'Artisanat', progress: 40, icon: <Scissors size={20} />, color: 'bg-pink-500' },
    { title: 'Service de livraison express', author: 'Kone M.', location: 'Abidjan, Yopougon', budget: '1 200 000 F', seeking: 'Chauffeur avec vehicule', category: 'Transport', progress: 80, icon: <Truck size={20} />, color: 'bg-orange-500' },
    { title: 'Studio photo & video', author: 'Jean-Luc K.', location: 'San Pedro', budget: '3 000 000 F', seeking: 'Photographe experimente', category: 'Creatif', progress: 25, icon: <Camera size={20} />, color: 'bg-purple-500' },
  ];

  const sampleServices = [
    { title: 'Plomberie & Sanitaire', provider: 'Moussa T.', price: '15 000 F/jour', rating: 4.8, reviews: 42, icon: <Wrench size={20} />, color: 'from-blue-400 to-blue-600' },
    { title: 'Peinture batiment', provider: 'Andre B.', price: '10 000 F/jour', rating: 4.9, reviews: 67, icon: <PaintBucket size={20} />, color: 'from-yellow-400 to-orange-500' },
    { title: 'Cours de maths', provider: 'Fatou L.', price: '5 000 F/heure', rating: 5.0, reviews: 23, icon: <GraduationCap size={20} />, color: 'from-green-400 to-emerald-600' },
    { title: 'Design graphique', provider: 'Kevin A.', price: '25 000 F/projet', rating: 4.7, reviews: 38, icon: <Layers size={20} />, color: 'from-purple-400 to-pink-500' },
    { title: 'Livraison marchandises', provider: 'Seydou K.', price: '3 000 F/course', rating: 4.6, reviews: 91, icon: <Truck size={20} />, color: 'from-teal-400 to-cyan-600' },
    { title: 'Coiffure a domicile', provider: 'Marie C.', price: '8 000 F', rating: 4.8, reviews: 55, icon: <Scissors size={20} />, color: 'from-rose-400 to-pink-600' },
  ];

  const sampleShops = [
    { name: 'INGCO Tools Duekoue', owner: 'KABA M.', products: 245, category: 'Quincaillerie & Outillage', rating: 4.9 },
    { name: 'C2CI Peintures', owner: 'Innov Com.', products: 180, category: 'Peintures & Enduits', rating: 4.7 },
    { name: 'TechnoShop CI', owner: 'Amara S.', products: 92, category: 'Electronique', rating: 4.8 },
    { name: 'Mode Elegance', owner: 'Clementine B.', products: 156, category: 'Pret-a-porter', rating: 4.6 },
  ];

  const testimonials = [
    { name: 'Amidou K.', role: 'Commercant, Abidjan', text: "Grace a DJAMANN, je gere mon stock et mes factures depuis mon telephone. C'est un vrai gain de temps ! Mon chiffre d'affaires a augmente de 30% en 3 mois.", avatar: 'AK' },
    { name: 'Fatou B.', role: 'Entrepreneure, Bouake', text: "J'ai trouve 2 partenaires fiables sur la plateforme pour lancer mon salon de coiffure. On s'est reparti les couts et aujourd'hui on tourne tres bien.", avatar: 'FB' },
    { name: 'Michel A.', role: 'Artisan, Daloa', text: "En tant que plombier, je recois des clients chaque semaine via DJAMANN. Je fais mes devis et factures directement sur l'application.", avatar: 'MA' },
    { name: 'Clarisse N.', role: 'Commercante, Man', text: "Avant DJAMANN, je notais tout dans un cahier. Maintenant mon stock est gere automatiquement et je ne perds plus d'argent.", avatar: 'CN' },
    { name: 'Ousmane D.', role: 'Livreur, Abidjan', text: "J'ai propose mon service de livraison et en 1 mois j'avais deja 15 clients reguliers. La plateforme est vraiment geniale !", avatar: 'OD' },
  ];

  const howItWorks = [
    { step: '01', title: 'Creez votre compte', desc: 'Inscription gratuite en 30 secondes avec votre email.', icon: <UserPlus size={24} /> },
    { step: '02', title: 'Configurez votre espace', desc: 'Ajoutez vos produits, services ou publiez votre projet.', icon: <Settings size={24} /> },
    { step: '03', title: 'Developpez votre activite', desc: 'Gerez, facturez et collaborez depuis votre tableau de bord.', icon: <TrendingUp size={24} /> },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ========== HERO ========== */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-blue-500">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
          </div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        </div>

        <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 lg:px-20 py-5">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DJAMANN" className="w-11 h-11 rounded-xl object-contain bg-white/15 backdrop-blur-sm p-0.5" />
            <span className="text-2xl font-bold text-white tracking-tight">DJAMANN</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button onClick={scrollToDiscover} className="px-4 py-2 text-white/80 hover:text-white text-sm font-medium transition-colors">Decouvrir</button>
            <button onClick={scrollToLogin} className="px-4 py-2 text-white/80 hover:text-white text-sm font-medium transition-colors">Se connecter</button>
            <Link to="/register" className="px-5 py-2.5 bg-white text-indigo-600 text-sm font-semibold rounded-xl hover:bg-white/90 transition-all shadow-lg shadow-indigo-900/20">Creer un compte</Link>
          </div>
          <div className="sm:hidden flex items-center gap-2">
            <button onClick={scrollToLogin} className="px-3 py-2 text-white/80 text-sm">Connexion</button>
            <Link to="/register" className="px-4 py-2 bg-white text-indigo-600 text-sm font-semibold rounded-xl">S'inscrire</Link>
          </div>
        </nav>

        <div className="relative z-10 flex-1 flex items-center px-6 md:px-12 lg:px-20 pb-16">
          <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold text-white leading-tight mb-3">
                Bienvenue sur <span className="text-orange-300">DJAMANN</span>
              </h1>
              <div className="h-12 sm:h-14 mb-6 overflow-hidden relative">
                {heroTexts.map((text, i) => (
                  <div key={i} className={`absolute inset-0 flex items-center justify-center lg:justify-start transition-all duration-500 ${i === heroTextIndex ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <span className="text-2xl sm:text-3xl font-bold text-white/80">{text}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-white/80 max-w-lg mb-10 mx-auto lg:mx-0 leading-relaxed">
                Pas de capital ? Pas de materiel ? <strong className="text-white">Pas de probleme !</strong> Trouvez des partenaires, gerez votre activite et construisez votre succes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <Link to="/register" className="group px-8 py-4 bg-white text-indigo-600 font-bold text-lg rounded-2xl hover:bg-yellow-300 hover:text-indigo-700 transition-all duration-300 shadow-2xl shadow-indigo-900/30 flex items-center justify-center gap-2">
                  Commencer gratuitement <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>

              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative mx-auto w-[320px]">
                <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl shadow-black/40">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-20"></div>
                  <div className={`bg-gradient-to-br ${screenshots[currentScreenshot].color} rounded-[2rem] overflow-hidden min-h-[420px] transition-all duration-500`}>
                    <div className="flex items-center justify-between px-6 pt-8 pb-2">
                      <span className="text-[10px] text-white/60 font-medium">14:35</span>
                      <div className="flex gap-1">
                        <div className="w-3 h-1.5 bg-white/40 rounded-full"></div>
                        <div className="w-3 h-1.5 bg-white/40 rounded-full"></div>
                        <div className="w-4 h-1.5 bg-white/60 rounded-full"></div>
                      </div>
                    </div>
                    <div className="px-4 pb-1">
                      <div className="text-white font-bold text-base">{screenshots[currentScreenshot].title}</div>
                      <div className="text-white/60 text-xs">{screenshots[currentScreenshot].desc}</div>
                    </div>
                    <div className="transition-all duration-500">{screenshots[currentScreenshot].content}</div>
                  </div>
                </div>
                <div className="flex justify-center gap-2 mt-6">
                  {screenshots.map((_, i) => (
                    <button key={i} onClick={() => setCurrentScreenshot(i)} className={`h-2 rounded-full transition-all duration-300 ${i === currentScreenshot ? 'bg-white w-8' : 'bg-white/30 w-2'}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex justify-center pb-6">
          <button onClick={scrollToDiscover} className="text-white/40 hover:text-white/80 transition-colors animate-bounce"><ChevronDown size={32} /></button>
        </div>
      </section>



      {/* ========== VIDEO PRESENTATION ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20  relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <AnimatedCard>
            <div className="text-center mb-10">
              
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
                Decouvrez <span className="text-orange-500">DJAMANN</span> en 2 minutes
              </h2>
              <p className="text-black/60 text-lg max-w-2xl mx-auto">Regardez notre video pour comprendre comment DJAMANN peut transformer votre activite.</p>
            </div>
          </AnimatedCard>

          <AnimatedCard delay={200}>
            <div className="relative max-w-3xl mx-auto">
              {/* Video container with glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl opacity-20 blur-xl"></div>
              <div className="relative bg-gray-950 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                {!showVideo ? (
                  <div className="relative aspect-video cursor-pointer group" onClick={() => setShowVideo(true)}>
                    {/* Miniature YouTube reelle */}
                    <img 
                      src={`https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`} 
                      alt="Presentation DJAMANN" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`; }}
                    />
                    {/* Overlay sombre au hover */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-all duration-300"></div>
                    {/* Bouton play */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-red-600/40 group-hover:scale-110 group-hover:bg-red-500 transition-all duration-300">
                        <Play size={36} className="text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white/80 text-xs font-medium">Presentation officielle</div>
                  </div>
                ) : (
                  <div className="aspect-video">
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
                      title="Presentation DJAMANN"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </div>
            </div>
          </AnimatedCard>

          {/* Quick info cards below video */}
          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto mt-8">
            {[
              { icon: <Clock size={18} />, text: 'Inscription en 30s' },
              { icon: <CreditCard size={18} />, text: '100% gratuit' },
              { icon: <ShieldCheck size={18} />, text: 'Donnees securisees' },
            ].map((item, i) => (
              <AnimatedCard key={i} delay={400 + i * 100}>
                <div className="flex items-center justify-center gap-2 bg-orange-100 backdrop-blur-sm border border-white/10 rounded-xl py-3 px-2 text-orange-600 text-xs sm:text-sm">
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TUTOS YOUTUBE ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-10">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Apprenez a utiliser <span className="text-indigo-600">DJAMANN</span>
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Des tutos pas-a-pas pour maitriser chaque fonctionnalite de la plateforme.</p>
            </div>
          </AnimatedCard>

          <AnimatedCard delay={200}>
            <div className="relative max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/videoseries?list=${YOUTUBE_PLAYLIST_ID}`}
                    title="Tutoriels DJAMANN"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </AnimatedCard>



          <AnimatedCard delay={500}>
            <div className="text-center mt-8">
              <a href={`https://www.youtube.com/playlist?list=${YOUTUBE_PLAYLIST_ID}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-200">
                <Youtube size={18} /> Voir tous les tutos sur YouTube
              </a>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== TUTOS WORKSPACE ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-slate-950 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-80 h-80 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-10 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedCard>
            <div className="text-center mb-10">

              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Maitrisez votre <span className="text-teal-300">Workspace</span>
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                Une playlist dediee pour apprendre a gerer vos produits, clients, factures, paiements et rapports depuis votre espace professionnel.
              </p>
            </div>
          </AnimatedCard>

          <AnimatedCard delay={200}>
            <div className="grid lg:grid-cols-[1.4fr_0.8fr] gap-6 items-stretch">
              <div className="bg-black rounded-2xl shadow-2xl shadow-teal-900/30 border border-white/10 overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/videoseries?list=${YOUTUBE_WORKSPACE_PLAYLIST_ID}`}
                    title="Tutoriels DJAMANN Workspace"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              <div className="bg-white/10 border border-white/10 rounded-2xl p-6 backdrop-blur-sm flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-teal-400/20 rounded-2xl flex items-center justify-center mb-5">
                    <Youtube size={24} className="text-teal-200" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Tutoriels pas-a-pas</h3>
                  <div className="space-y-3 text-sm text-white/75">
                    {['Comprendre le tableau de bord', 'Gerer les produits et le stock', 'Creer devis, factures et paiements', 'Suivre les rapports et performances'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle size={16} className="text-teal-300 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <a
                  href={`https://www.youtube.com/playlist?list=${YOUTUBE_WORKSPACE_PLAYLIST_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-950/30"
                >
                  <Youtube size={18} /> Voir la playlist de la formation Workspace
                </a>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== COMMENT CA MARCHE ========== */}
      <section id="decouvrir" className="py-20 px-6 md:px-12 lg:px-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-14">
             
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Comment ca marche ?</h2>
              <p className="text-gray-500 text-lg max-w-xl mx-auto">3 etapes suffisent pour demarrer votre activite sur DJAMANN.</p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-indigo-200 via-purple-200 to-pink-200"></div>
            {howItWorks.map((step, i) => (
              <AnimatedCard key={i} delay={i * 200}>
                <div className="relative text-center">
                  <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 shadow-lg shadow-indigo-200">{step.icon}</div>
                  <span className="absolute -top-2 -right-2 md:right-auto md:left-[calc(50%+20px)] md:-top-1 w-7 h-7 bg-yellow-400 text-gray-900 text-xs font-bold rounded-full flex items-center justify-center shadow">{step.step}</span>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm">{step.desc}</p>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FONCTIONNALITES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-14">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Tout ce qu'il faut pour <span className="text-indigo-600">reussir</span></h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Une plateforme complete pour lancer, gerer et developper votre activite.</p>
            </div>
          </AnimatedCard>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <AnimatedCard key={i} delay={i * 100}>
                <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-gray-100 hover:border-indigo-200 transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">{f.icon}</div>
                  <h3 className="text-gray-900 font-bold text-lg mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PROJETS / OPPORTUNITES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-4">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Des projets qui cherchent des <span className="text-indigo-600">partenaires</span></h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Voici des exemples d'opportunites disponibles sur la plateforme. Trouvez le projet qui vous correspond !</p>
            </div>
          </AnimatedCard>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {sampleProjects.map((p, i) => (
              <AnimatedCard key={i} delay={i * 120}>
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className={`${p.color} p-4 flex items-center gap-3`}>
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-white">{p.icon}</div>
                    <div>
                      <span className="text-[10px] text-white/70 uppercase tracking-wider font-medium">{p.category}</span>
                      <h4 className="text-white font-bold text-sm leading-tight">{p.title}</h4>
                    </div>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3"><MapPin size={12} /> {p.location}</div>
                    <div className="text-xs text-gray-600 mb-2"><strong>Budget :</strong> {p.budget}</div>
                    <div className="text-xs text-gray-600 mb-4"><strong>Recherche :</strong> {p.seeking}</div>
                    <div className="mt-auto">
                      <div className="flex justify-between text-[10px] text-gray-500 mb-1"><span>Avancement</span><span>{p.progress}%</span></div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${p.color} rounded-full transition-all duration-1000`} style={{ width: `${p.progress}%` }}></div>
                      </div>
                    </div>
                    <span className="text-[10px] text-gray-400 mt-2">par {p.author}</span>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
          <AnimatedCard delay={500}>
            <div className="text-center mt-10">
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200">Voir toutes les opportunites <ArrowRight size={16} /></Link>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== SERVICES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-4">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Des <span className="text-teal-600">professionnels</span> a votre service</h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Plombiers, peintres, designers, professeurs... Trouvez le bon prestataire ou proposez vos competences.</p>
            </div>
          </AnimatedCard>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {sampleServices.map((s, i) => (
              <AnimatedCard key={i} delay={i * 100}>
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 p-5 transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 bg-gradient-to-br ${s.color} rounded-xl flex items-center justify-center text-white shadow-sm`}>{s.icon}</div>
                    <div>
                      <h4 className="font-bold text-gray-900">{s.title}</h4>
                      <span className="text-xs text-gray-500">par {s.provider}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-indigo-600 font-bold text-sm">{s.price}</span>
                    <div className="flex items-center gap-1">
                      <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-medium text-gray-700">{s.rating}</span>
                      <span className="text-xs text-gray-400">({s.reviews})</span>
                    </div>
                  </div>
                  <button className="w-full py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">Voir le profil</button>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BOUTIQUES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-4">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Des <span className="text-pink-600">boutiques</span> deja actives</h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Creez votre boutique en ligne ou parcourez celles de nos entrepreneurs.</p>
            </div>
          </AnimatedCard>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {sampleShops.map((shop, i) => (
              <AnimatedCard key={i} delay={i * 120}>
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className={`h-20 bg-gradient-to-r ${i === 0 ? 'from-blue-500 to-indigo-500' : i === 1 ? 'from-orange-400 to-red-500' : i === 2 ? 'from-cyan-500 to-blue-500' : 'from-pink-400 to-purple-500'} relative`}>
                    <div className="absolute -bottom-6 left-4">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center text-lg font-bold text-gray-700">{shop.name.charAt(0)}</div>
                    </div>
                  </div>
                  <div className="p-4 pt-8">
                    <h4 className="font-bold text-gray-900 text-sm mb-0.5">{shop.name}</h4>
                    <span className="text-[11px] text-gray-400">par {shop.owner}</span>
                    <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Package size={12} /> {shop.products} produits</span>
                      <span className="flex items-center gap-1"><Star size={12} className="text-yellow-400 fill-yellow-400" /> {shop.rating}</span>
                    </div>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full">{shop.category}</span>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
          <AnimatedCard delay={500}>
            <div className="text-center mt-10">
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-pink-600 text-white font-semibold rounded-xl hover:bg-pink-700 transition-all shadow-lg shadow-pink-200">Creer ma boutique gratuitement <ArrowRight size={16} /></Link>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== OUTILS DE GESTION ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-12">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Des outils <span className="text-purple-600">professionnels</span> dans votre poche</h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">Tableau de bord, gestion de stock, facturation... Tout est inclus, rien a installer.</p>
            </div>
          </AnimatedCard>
          <div className="relative">
            <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide -mx-6 px-6">
              {screenshots.map((screen, i) => (
                <div key={i} className="snap-center flex-shrink-0 w-[300px] sm:w-[340px]">
                  <div className={`bg-gradient-to-br ${screen.color} rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300`}>
                    <div className="px-4 pt-4 pb-1">
                      <div className="text-white font-bold">{screen.title}</div>
                      <div className="text-white/60 text-xs">{screen.desc}</div>
                    </div>
                    {screen.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== TEMOIGNAGES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-12">
  
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Ils ont <span className="text-indigo-600">reussi</span> avec DJAMANN</h2>
            </div>
          </AnimatedCard>
          <div className="relative max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 min-h-[260px] flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (<Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />))}
                </div>
                <p className="text-gray-600 text-lg italic leading-relaxed mb-6 transition-all duration-500">"{testimonials[currentTestimonial].text}"</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">{testimonials[currentTestimonial].avatar}</div>
                <div>
                  <p className="font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-sm text-gray-400">{testimonials[currentTestimonial].role}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 mt-6">
              <button onClick={() => setCurrentTestimonial(p => (p - 1 + testimonials.length) % testimonials.length)} className="w-10 h-10 bg-white rounded-full shadow flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:shadow-lg transition-all"><ChevronLeft size={18} /></button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (<button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-2 rounded-full transition-all duration-300 ${i === currentTestimonial ? 'bg-indigo-500 w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'}`} />))}
              </div>
              <button onClick={() => setCurrentTestimonial(p => (p + 1) % testimonials.length)} className="w-10 h-10 bg-white rounded-full shadow flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:shadow-lg transition-all"><ChevronRight size={18} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PARTENAIRES ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-12">

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Ils nous font <span className="text-indigo-600">confiance</span></h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">DJAMANN collabore avec des acteurs majeurs pour soutenir l'entrepreneuriat en Afrique.</p>
            </div>
          </AnimatedCard>

          {/* Logos defilants */}
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-l from-white to-transparent z-10"></div>
            <div className="flex gap-8 sm:gap-12 animate-scroll">
              {[...Array(2)].map((_, setIdx) => (
                <div key={setIdx} className="flex gap-8 sm:gap-12 shrink-0 items-center">
                  {[
                    { name: 'INGCO MAN', logo: '/logo/INGCO MAN.jpg' },
                    { name: 'INOV Communication', logo: '/logo/INOV Communication.png' },
                    { name: 'Djater International Services', logo: '/logo/djater international services.jpg' },
                    { name: 'Djater Voyage', logo: '/logo/djater voyage.jpg' },
                    { name: 'Legourou', logo: '/logo/legourou.jpg' },
                    { name: 'Tchiming Technology', logo: '/logo/tchiming technology.jpg' },
                  ].map((partner, i) => (
                    <div key={i} className="flex flex-col items-center gap-2 group cursor-default">
                      <div className="w-32 h-32 bg-white rounded-2xl shadow-md group-hover:scale-110 group-hover:shadow-xl transition-all duration-300 flex items-center justify-center p-3 border border-gray-100">
                        <img src={partner.logo} alt={partner.name} className="w-full h-full object-contain rounded-lg" />
                      </div>
                      <span className="text-xs text-gray-400 font-medium group-hover:text-gray-600 transition-colors whitespace-nowrap">{partner.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <AnimatedCard delay={300}>
            <div className="mt-14 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100 text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Devenez partenaire DJAMANN</h3>
              <p className="text-gray-500 text-sm mb-4 max-w-lg mx-auto">Vous etes une entreprise, une ONG ou une institution ? Collaborons pour soutenir les entrepreneurs africains.</p>
              <a href="mailto:partenariat@djamann.com" className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200">
                <Handshake size={18} /> Nous contacter
              </a>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full mb-4 uppercase tracking-wider">FAQ</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Questions frequentes</h2>
              <p className="text-gray-500 text-lg">Tout ce que vous devez savoir avant de commencer.</p>
            </div>
          </AnimatedCard>
          <div className="space-y-4">
            {[
              { q: "Est-ce que DJAMANN est vraiment gratuit ?", a: "Oui ! L'inscription et les fonctionnalites de base sont 100% gratuites. Vous pouvez creer votre projet, gerer vos produits et collaborer directement." },
              { q: "J'ai pas de capital, je peux quand meme lancer un business ?", a: "C'est exactement le but de DJAMANN ! Publiez votre projet et trouvez des partenaires prets a investir ou a apporter du materiel. Ensemble, vous etes plus forts." },
              { q: "Comment trouver des partenaires fiables ?", a: "Chaque utilisateur a un profil avec ses projets et activités. Vous pouvez echanger directement et verifier avant de collaborer." },
              { q: "Est-ce que je peux utiliser DJAMANN sur mon telephone ?", a: "Absolument ! La plateforme est optimisee pour mobile. Gerez votre activite depuis n'importe quel appareil, partout dans le monde." },
              { q: "Comment je recois mes paiements ?", a: "Vous gerez vos paiements directement avec vos clients. DJAMANN vous fournit les outils de facturation et de suivi pour professionnaliser vos transactions." },
            ].map((faq, i) => (
              <AnimatedCard key={i} delay={i * 80}>
                <details className="group bg-gray-50 rounded-2xl border border-gray-100 hover:border-indigo-200 transition-colors">
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <HelpCircle size={16} className="text-indigo-600" />
                      </div>
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">{faq.q}</span>
                    </div>
                    <ChevronDown size={18} className="text-gray-400 group-open:rotate-180 transition-transform duration-300 flex-shrink-0 ml-2" />
                  </summary>
                  <div className="px-5 pb-5 pl-16 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              </AnimatedCard>
            ))}
          </div>
          <AnimatedCard delay={500}>
            <div className="text-center mt-10">
              <p className="text-gray-500 text-sm mb-3">Une autre question ?</p>
              <a href="mailto:support@djamann.com" className="inline-flex items-center gap-2 text-indigo-600 font-semibold text-sm hover:text-indigo-800 transition-colors">
                <MessageCircle size={16} /> Contactez notre equipe
              </a>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ========== CONNEXION ========== */}
      <section ref={loginFormRef} className="py-20 px-6 md:px-12 lg:px-20 bg-gray-50" id="connexion">
        <div className="max-w-5xl mx-auto">
          <AnimatedCard>
            <div className="text-center mb-12">

              <p className="text-gray-500 text-lg">Connectez-vous ou creez votre compte en quelques secondes.</p>
            </div>
          </AnimatedCard>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <AnimatedCard className="hidden lg:block">
              <div className="pt-4">

                <p className="text-gray-500 mb-8">Retrouvez vos projets, collaborations et pilotez votre activite.</p>
                <div className="space-y-3 mb-8">
                  {['Accedez a votre tableau de bord', 'Gerez vos produits et services', 'Suivez vos finances en temps reel', 'Collaborez avec votre equipe'].map((t, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-600">
                      <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><CheckCircle size={14} className="text-green-600" /></div>
                      <span className="text-sm">{t}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                  <div className="flex items-center gap-3 mb-3">
                    <Lightbulb size={20} className="text-yellow-500" />
                    <span className="text-indigo-800 font-semibold text-sm">Pas encore inscrit ?</span>
                  </div>
                  <p className="text-indigo-600 text-sm mb-3">Rejoignez {countEntrepreneurs.toLocaleString()}+ entrepreneurs qui font confiance a DJAMANN.</p>
                  <Link to="/register" className="inline-flex items-center gap-2 text-indigo-600 font-semibold text-sm hover:text-indigo-800 transition-colors">Creer un compte gratuit <ArrowRight size={16} /></Link>
                </div>
              </div>
            </AnimatedCard>

            <AnimatedCard delay={200}>
              <div>
                <div className="lg:hidden text-center mb-6">
                  <img src="/logo.png" alt="DJAMANN" className="w-14 h-14 mx-auto mb-3 rounded-2xl shadow-lg object-contain" />
                  <h3 className="text-xl font-bold text-gray-900">Se connecter !</h3>
                  <p className="text-gray-500 text-sm">Connectez-vous a votre espace</p>
                </div>
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                  <div className="hidden lg:block text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Connexion</h3>
                    <p className="text-gray-400 text-sm mt-1">Accedez a votre espace DJAMANN</p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2"><Mail size={16} className="inline mr-1" /> Adresse email</label>
                      <input type="email" placeholder="nom@exemple.com" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2"><Lock size={16} className="inline mr-1" /> Mot de passe</label>
                      <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} placeholder="" value={mot_de_passe} onChange={e => setMotDePasse(e.target.value)} required className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white pr-12" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">

                      <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">Mot de passe oublie ?</Link>
                    </div>

                    {error && (
                      <div className={`p-4 border rounded-xl flex items-start gap-3 ${emailNotVerified ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : error.includes('suspendu') ? 'bg-orange-50 border-orange-200 text-orange-800' : error.includes('supprime') ? 'bg-gray-100 border-gray-300 text-gray-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        <span className="text-xl flex-shrink-0">{emailNotVerified ? <Mail size={20} /> : error.includes('suspendu') ? <Ban size={20} /> : error.includes('supprime') ? <XCircle size={20} /> : <AlertTriangle size={20} />}</span>
                        <div className="flex-1">
                          <span className="text-sm font-medium block">{error}</span>
                          {emailNotVerified && !resendSuccess && (
                            <button type="button" onClick={handleResendVerification} disabled={resendLoading} className="text-xs underline mt-2 inline-block hover:opacity-80 text-yellow-700 disabled:opacity-50">
                              {resendLoading ? <><Clock size={14} className="inline mr-1" /> Envoi...</> : <><RefreshCw size={14} className="inline mr-1" /> Renvoyer l'email</>}
                            </button>
                          )}
                          {resendSuccess && <span className="text-xs text-green-600 mt-2 block"><CheckCircle size={14} className="inline mr-1" /> Email envoye !</span>}
                          {(error.includes('suspendu') || error.includes('supprime')) && <a href="mailto:support@djamann.com" className="text-xs underline mt-2 inline-block hover:opacity-80"><Mail size={14} className="inline mr-1" /> Contacter le support</a>}
                        </div>
                      </div>
                    )}

                    <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-indigo-200 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5">
                      {loading ? (
                        <span className="flex items-center justify-center gap-3">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Connexion en cours...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">Se connecter <ArrowRight size={18} /></span>
                      )}
                    </button>
                  </form>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-400">Nouveau sur DJAMANN ?</span></div>
                  </div>
                  <Link to="/register" className="w-full py-3.5 border-2 border-indigo-200 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-300 flex items-center justify-center gap-2"><Sparkles size={18} /> Creer un compte gratuitement</Link>
                </div>
                <div className="mt-6 flex items-center justify-center gap-6 text-gray-400 text-sm">
                  <div className="flex items-center gap-1"><Lock size={14} /> Securise</div>
                  <div className="flex items-center gap-1"><Zap size={14} /> Rapide</div>
                  <div className="flex items-center gap-1"><Globe size={14} /> Mondial</div>
                </div>

                {/* Lien vers le portail Workspace */}
                <a
                  href="/portal/workspace"
                  className="mt-5 flex items-center justify-between gap-3 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border-2 border-teal-200/70 rounded-2xl hover:border-teal-300 hover:shadow-lg hover:shadow-teal-100/50 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md shadow-teal-300/40 group-hover:scale-105 transition-transform">
                      <Briefcase size={18} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-800">DJAMANN Workspace</div>
                      <div className="text-xs text-gray-500">Accédez à votre portail professionnel dédié</div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-teal-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </a>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="bg-gray-900 text-white/60 py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="DJAMANN" className="w-9 h-9 rounded-lg object-contain" />
              <div>
                <span className="text-white font-semibold block">DJAMANN</span>
                <span className="text-xs text-white/40">La plateforme qui democratise l'entrepreneuriat</span>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/guide" className="hover:text-white transition-colors flex items-center gap-1"><BookOpen size={14} /> Guide</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Conditions</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Confidentialite</Link>
            </div>
            <p className="text-sm">&copy; {new Date().getFullYear()} DJAMANN. Tous droits reserves.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
